import React, { useState, useEffect } from 'react';
import './AdminLogin.css';

const AdminLogin = ({ onLogin, isLoading = false }) => {
  const [formData, setFormData] = useState({
    username: 'admin',
    password: ''
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Clear error when user starts typing
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please enter both username and password');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/admin-auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token in localStorage
        localStorage.setItem('travel-futura-token', data.data.token);
        localStorage.setItem('travel-futura-user', JSON.stringify(data.data.user));
        
        // Call parent callback
        if (onLogin) {
          onLogin(data.data);
        }
      } else {
        // Handle specific error for missing admin table
        if (data.code === 'TABLE_NOT_EXISTS') {
          setError('🔄 Admin system is initializing. Please wait a moment and try again.');
        } else {
          setError(data.message || 'Login failed. Please try again.');
        }
        // Clear password on error
        setFormData(prev => ({ ...prev, password: '' }));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please check your connection and try again.');
      setFormData(prev => ({ ...prev, password: '' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Auto-focus password field since username is pre-filled
  useEffect(() => {
    const passwordInput = document.getElementById('password');
    if (passwordInput) {
      passwordInput.focus();
    }
  }, []);

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="login-header">
          <img
            src={`${process.env.PUBLIC_URL}/future-objects-logo.png`}
            alt="Future Objects"
            className="app-logo"
            style={{ width: '100%', maxWidth: 220, height: 'auto', display: 'block', margin: '0 auto 12px' }}
          />
          <p className="subtitle">Admin Dashboard Access</p>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username" className="form-label">
              Username or Email
            </label>
            <input
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={isSubmitting || isLoading}
              autoComplete="username"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="form-input"
              required
              disabled={isSubmitting || isLoading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className={`login-button ${isSubmitting ? 'loading' : ''}`}
            disabled={isSubmitting || isLoading || !formData.username || !formData.password}
          >
            {isSubmitting ? (
              <>
                <span className="loading-spinner"></span>
                Signing In...
              </>
            ) : (
              'Sign In to Dashboard'
            )}
          </button>
        </form>

        <div className="login-features">
          <div className="feature-icons">
            <span className="feature-icon" title="Multi-language Support">🌐</span>
            <span className="feature-icon" title="AI-Powered">🤖</span>
            <span className="feature-icon" title="Travel Management">✈️</span>
            <span className="feature-icon" title="Analytics">📊</span>
          </div>
        </div>

        <div className="login-footer">
          <p>Future Objects Admin Panel</p>
          <p>Secure access to your travel management system</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
